import React, { ReactNode, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Keyboard, TextInput } from 'react-native';
import { useSharedValue, withTiming } from 'react-native-reanimated';

import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';

import { Screen } from '@/components/ScreenComponent';
import { Box } from '@/components/lib/components';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useDebounce } from '@/hooks/use-debounce';

import { SearchBar } from '../components/SearchBar';

const FADE_DURATION = 300;
const DEBOUNCE_MS = 250;

type SearchScreenContextValue = {
    query: string;
    debouncedQuery: string;
    collapseSearchBar: () => void;
};

const SearchScreenContext = createContext<SearchScreenContextValue | null>(null);

export const useSearchScreen = () => {
    const context = useContext(SearchScreenContext);
    if (!context) {
        throw new Error('useSearchScreen must be used within a SearchScreenShell');
    }
    return context;
};

type SearchScreenShellProps = {
    minQueryLength?: number;
    children: ReactNode;
};

export const SearchScreenShell = ({ minQueryLength = 0, children }: SearchScreenShellProps) => {
    const [query, setQuery] = useState('');
    const inputRef = useRef<TextInput>(null);
    const navigation = useNavigation();
    const route = useRoute();

    const debounced = useDebounce(DEBOUNCE_MS, query);
    const debouncedQuery = debounced.length >= minQueryLength ? debounced : '';

    const fadeInOpacity = useSharedValue(0);

    const animateIn = useCallback(() => {
        fadeInOpacity.value = withTiming(1, { duration: FADE_DURATION });
    }, [fadeInOpacity]);

    const animateOut = useCallback(() => {
        fadeInOpacity.value = withTiming(0, { duration: FADE_DURATION });
    }, [fadeInOpacity]);

    useFocusEffect(
        useCallback(() => {
            animateIn();
            const timeout = setTimeout(() => inputRef.current?.focus(), FADE_DURATION);
            return () => {
                clearTimeout(timeout);
                // clear the query & animate out only when the user actually leaves the search tab
                const tabState = navigation.getState();
                const searchTabActive = tabState?.routes[tabState.index]?.key === route.key;
                if (!searchTabActive) {
                    setQuery('');
                    animateOut();
                }
            };
        }, [animateIn, animateOut, navigation, route.key])
    );

    const onPressCancel = useCallback(() => {
        Keyboard.dismiss();
        setQuery('');
        animateOut();
    }, [animateOut]);

    const contextValue = useMemo(
        () => ({ query, debouncedQuery, collapseSearchBar: animateOut }),
        [query, debouncedQuery, animateOut]
    );

    return (
        <Screen>
            <MaxWidthWrapper>
                <Box paddingLeft={'s16'} pt={'s8'} pb={'s8'}>
                    <SearchBar
                        setQuery={setQuery}
                        query={query}
                        inputRef={inputRef}
                        animatedValue={fadeInOpacity}
                        onPressCancel={onPressCancel}
                        onFocus={animateIn}
                        onSubmitEditing={animateOut}
                    />
                </Box>
            </MaxWidthWrapper>
            <Box flex={1}>
                <SearchScreenContext.Provider value={contextValue}>{children}</SearchScreenContext.Provider>
            </Box>
        </Screen>
    );
};
